import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { UserDataService } from '@com-delta-idp/mnx-oauth';
import {
  adobeClickEventAnalyticsMocks,
  ANALYTICS_EVENTS,
  HEADER_MENU,
  LoggingService
} from '@com-delta-idp/mnx-utils';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DeviceTypeHelper } from '../../common/device-type-helper';
import { ErrorHandlerDirective } from '../../common/error-handler/error-handler.directive';
import { NgcSelectDirective } from '../../common/select.directive';
import { ApplicationConfig } from '../../config/config';
import { ContentCheckIn } from '../../model/content/contentcheckin.model';
import { AdobeDataLayerService } from '../../services/adobe-data-layer/adobe-data-layer';
import { ContentHeaderFooterService } from '../../services/content/contentheaderfooter.service';
import { FormStoreDirective } from '../../store/directive/form-store.directive';

@Component({
  selector: 'ngc-checkin',
  templateUrl: 'navigation-checkin.component.html'
})
export class NavigationCheckinComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(ErrorHandlerDirective) errorHandler: ErrorHandlerDirective;
  @ViewChild('formStoreDir') formStoreDir: FormStoreDirective;
  @ViewChild('checkinByDropdown') checkinByDropdown: NgcSelectDirective;
  isSubmitted: boolean;
  public confirmationNumber: string;
  public eticketNumber: string;
  public skymilesNumber: string;
  public creditCardNumber: number;
  public lastName: string;
  public checkinWidgetForm: UntypedFormGroup;
  public fromAirport = '';
  public findYourTripByLabel: string;
  public formAction = '/PCCOciWeb/processFindPnr.action1';
  public checkInSubmitUrl: string;
  checkinBy: any[];
  selectCheckinBy = '';
  whereToFocusEnable = false;
  checkinHelpFocusEnable = false;
  contentServiceHeaderFooterSubscribe: Subscription;
  deviceTypeSubscribe: Subscription;
  public contentCheckIn: ContentCheckIn;
  public deviceTypes: any = [];
  public credCardCode;
  @Input() widgetOnTop = false;
  @Output() errorPresent: EventEmitter<boolean> = new EventEmitter<boolean>();
  private unsubscribe: Subject<void> = new Subject();
  private isViewInitialized = false;

  constructor(
    private fb: UntypedFormBuilder,
    private cdf: ChangeDetectorRef,
    private contentService: ContentHeaderFooterService,
    private config: ApplicationConfig,
    private _deviceTypeHelper: DeviceTypeHelper,
    private loggingService: LoggingService,
    private adobeDataLayer: AdobeDataLayerService,
    private userDataService: UserDataService
  ) {}

  ngOnInit() {
    this.userDataService.getAuthConfigContent().subscribe((authConfig) => {
      this.checkinWidgetForm = this.fb.group({
        confirmationNumber: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
            Validators.pattern('^[a-zA-Z0-9]+$')
          ]
        ],
        eticketNumber: [
          '',
          [
            Validators.required,
            Validators.maxLength(14),
            Validators.minLength(13),
            Validators.pattern('^[0-9]+$')
          ]
        ],
        skymilesNumber: [
          '',
          [
            Validators.required,
            Validators.maxLength(10),
            Validators.pattern('^[0-9]+$')
          ]
        ],
        creditcardNumber: [
          '',
          [
            Validators.required,
            Validators.maxLength(16),
            Validators.minLength(10),
            Validators.pattern('^[0-9]+$')
          ]
        ],
        departureAirport: ['', [Validators.required]],
        lastName: [
          '',
          this.contentService.getValidator(authConfig, 'LAST_NAME_REGEX')
        ],
        searchType: new UntypedFormControl()
      });
    });

    const storeSyncComplete =
      this.formStoreDir?.syncFromStoreComplete.subscribe(
        () => {
          const formValue = this.formStoreDir.formValueRef;
          if (formValue) {
            this.fromAirport =
              formValue['departureAirport'] &&
              this.fromAirport !== formValue['departureAirport']
                ? formValue['departureAirport']
                : '';
            if (
              formValue['searchType'] &&
              this.selectCheckinBy !== formValue['searchType']
            ) {
              this.selectCheckinBy = formValue['searchType']
                ? formValue['searchType']
                : '';
              this.selectTripType(this.selectCheckinBy);
            }
            storeSyncComplete.unsubscribe();
          }
        },
        (err) => {
          this.loggingService.log('Caught Error: ' + err);
        }
      );

    if (this.contentService.contentHeaderFooter) {
      this.contentServiceHeaderFooterSubscribe =
        this.contentService.contentHeaderFooter
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(
            (data) => {
              if (data) {
                this.contentCheckIn =
                  data.header.primaryNavigations.get('check_in').details;
                this.formAction = this.contentCheckIn.submitUrl;
                this.setAemValues();
                this.cdf.detectChanges();
                if (this.contentCheckIn) {
                  if (this.contentCheckIn) {
                    this.setFlagsForFocusElements(this.contentCheckIn);
                    this.checkinBy = Array.from(
                      this.contentCheckIn.checkIn.values()
                    );
                    if (this.selectCheckinBy === '') {
                      this.selectCheckinBy = this.checkinBy[0]['value'];
                      this.selectTripType(this.selectCheckinBy);
                    }
                  }
                }
                if (this.isViewInitialized && this.checkinByDropdown) {
                  this.checkinByDropdown.reBind();
                }
              }
            },
            (err) => {
              this.loggingService.log('Caught Error: ' + err);
            }
          );
    }
    if (this.config.onBrowser) {
      this.deviceTypeSubscribe = this._deviceTypeHelper
        .deviceDetection()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((resp) => {
          this.deviceTypes = resp;
        });
    }
  }

  setFlagsForFocusElements(contentData) {
    if (contentData) {
      this.whereToFocusEnable =
        contentData.whereToFindLabel !== undefined &&
        contentData.whereToFindLabel !== ''
          ? true
          : false;
      this.checkinHelpFocusEnable =
        contentData.helpLabel !== undefined && contentData.helpLabel !== ''
          ? true
          : false;
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
  }

  getSelectedIndex() {
    if (
      this.selectCheckinBy &&
      this.selectCheckinBy.trim() !== '' &&
      this.checkinBy
    ) {
      const selectedIndex = this.checkinBy.findIndex(
        (value) => value.value === this.selectCheckinBy
      );
      return selectedIndex > -1 ? selectedIndex : 0;
    }
    return 0;
  }

  customChangeEventHandler(val) {
    this.isSubmitted = false;
    this.errorHandler.resetTheForm();
    this.selectTripType(val);
  }

  removeAllFormControl() {
    if (this.checkinWidgetForm.get('eticketNumber')) {
      this.checkinWidgetForm.removeControl('eticketNumber');
    }
    if (this.checkinWidgetForm.get('confirmationNumber')) {
      this.checkinWidgetForm.removeControl('confirmationNumber');
    }
    if (this.checkinWidgetForm.get('skymilesNumber')) {
      this.checkinWidgetForm.removeControl('skymilesNumber');
    }
    if (this.checkinWidgetForm.get('creditcardNumber')) {
      this.checkinWidgetForm.removeControl('creditcardNumber');
    }
    if (this.checkinWidgetForm.get('lastName')) {
      this.checkinWidgetForm.removeControl('lastName');
    }
    // if (this.checkinWidgetForm.get('departureAirport')) {
    //   this.checkinWidgetForm.removeControl('departureAirport');
    //   this.fromAirport = '';
    // }
  }

  selectTripType(val) {
    this.userDataService.getAuthConfigContent().subscribe((authConfig) => {
      this.selectCheckinBy = val;
      // let departureAirport = new UntypedFormControl(this.fromAirport, Validators.required);
      this.removeAllFormControl();
      // let departureAirport = new UntypedFormControl(this.fromAirport, Validators.required);
      if (val === 'eticketInput') {
        const eticketNumber = new UntypedFormControl(
          this.formStoreDir?.getStoredValueFor('eticketNumber'),
          [
            Validators.required,
            Validators.maxLength(14),
            Validators.minLength(13),
            Validators.pattern('^[0-9]+$')
          ]
        );
        this.checkinWidgetForm.addControl('eticketNumber', eticketNumber);
        // this.checkinWidgetForm.addControl('departureAirport', departureAirport);
      } else if (this.selectCheckinBy === 'confirmationInput') {
        const confirmationNumber = new UntypedFormControl(
          this.formStoreDir?.getStoredValueFor('confirmationNumber'),
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
            Validators.pattern('^[a-zA-Z0-9]+$')
          ]
        );
        this.checkinWidgetForm.addControl(
          'confirmationNumber',
          confirmationNumber
        );
        // this.checkinWidgetForm.addControl('departureAirport', departureAirport);
      } else if (this.selectCheckinBy === 'skymilesInput') {
        const skymilesNumber = new UntypedFormControl(
          this.formStoreDir?.getStoredValueFor('skymilesNumber'),
          [
            Validators.required,
            Validators.maxLength(10),
            Validators.pattern('^[0-9]+$')
          ]
        );
        this.checkinWidgetForm.addControl('skymilesNumber', skymilesNumber);
        // this.checkinWidgetForm.addControl('departureAirport', departureAirport);
      } else if (this.selectCheckinBy === 'creditCardInput') {
        const creditcardNumber = new UntypedFormControl(
          this.formStoreDir?.getStoredValueFor('creditcardNumber'),
          [
            Validators.required,
            Validators.maxLength(16),
            Validators.minLength(10),
            Validators.pattern('^[0-9]+$')
          ]
        );
        this.checkinWidgetForm.addControl('creditcardNumber', creditcardNumber);
        const lastName = new UntypedFormControl(
          this.formStoreDir?.getStoredValueFor('lastName'),
          this.contentService.getValidator(authConfig, 'LAST_NAME_REGEX')
        );
        this.checkinWidgetForm.addControl('lastName', lastName);
        // this.checkinWidgetForm.addControl('departureAirport', departureAirport);
      }
    });
  }

  markAsTouchedFrom() {
    (
      this.checkinWidgetForm as UntypedFormGroup
    ).controls.departureAirport.setValue(this.fromAirport);
    (
      this.checkinWidgetForm as UntypedFormGroup
    ).controls.departureAirport.markAsTouched({ onlySelf: true });
    this.checkinWidgetForm.controls.departureAirport.markAsTouched({
      onlySelf: true
    });
  }

  setAemValues() {
    this.fromAirport = this.contentCheckIn.fromAirport;
  }

  onSubmit(e) {
    const data = {
      ...adobeClickEventAnalyticsMocks,
      event: ANALYTICS_EVENTS.CLICK,
      event_detail: ANALYTICS_EVENTS.CLICK,
      click: {
        name: 'check in:submit',
        type: 'button',
        page_section: HEADER_MENU.CHECK_IN.PAGE_SECTION,
        action: this.formAction || ''
      }
    };

    this.adobeDataLayer.setUserDataInAnalytics(data);

    if (this.checkinWidgetForm.valid) {
      this.navigateToCheckIn(e);
    } else {
      this.isSubmitted = true;
      this.errorPresent.emit(true);
      this.errorHandler.validateForm(true);
    }
  }

  navigateToCheckIn(e) {
    if (this.checkInSubmitUrl) {
      this.formAction = this.checkInSubmitUrl;
    }
    e.target.action = this.formAction;
    e.target.submit();
    // window.location.href = this.formAction;
  }

  creditCardTypeDetected(event) {
    this.credCardCode = event;
  }

  ngOnDestroy() {
    if (this.contentServiceHeaderFooterSubscribe) {
      this.contentServiceHeaderFooterSubscribe.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribe.unsubscribe();
    if (this.deviceTypeSubscribe) {
      this.deviceTypeSubscribe.unsubscribe();
    }
    this.cdf.detach();
  }
}
